import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { theme } from '../../styles';
import { HelpIllustration } from '../../assets/illustrations';
import { Headings } from '../Headings';
import { Box } from '../Layout';
import { ToggleIcon } from './FAQDrawer';

type FAQModalContainerProps = {
  children: React.ReactNode;
};

const ModalContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  right: 0;
  background: ${theme.colors.white};
  overflow-y: auto;
  z-index: ${theme.zIndex.modal};
  width: 100%;
  height: 100%;
`;

const ModalHeader = styled(Box)`
  position: sticky; /* NOT SUPPORTED BY IE11 */
  top: 0;
  left: 0;
  right: 0;
  background-color: ${theme.colors.white};
  z-index: ${theme.zIndex.modal + 1};
`;

const ToggleButton = styled.button`
  display: flex;
  position: fixed;
  bottom: 1.25rem;
  right: 1.25rem;
  padding: 0;
  border: none;
  background: transparent;
  appearance: none;
  user-select: none;
`;

const ModalBody = styled.div`
  border-bottom: 1px solid ${theme.colors.lightGrey};
  margin-bottom: 1.25rem;
`;

export const FAQModalContainer = ({ children }: FAQModalContainerProps) => {
  const modalContainer = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const onToggle = () => {
    if (!isOpen) {
      modalContainer.current?.focus();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (modalContainer.current) {
      if (isOpen) {
        disableBodyScroll(modalContainer.current);
      } else {
        enableBodyScroll(modalContainer.current);
      }
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  });

  return (
    <>
      <ModalContainer
        ref={modalContainer}
        initial={{ height: 0 }}
        animate={{ height: isOpen ? '100%' : 0 }}
        transition={{ duration: 0.2 }}
        tabIndex={isOpen ? 0 : -1}
      >
        {isOpen && (
          <>
            <ModalHeader paddingTop={20} paddingRight={20} paddingBottom={40} paddingLeft={20} textAlign="center">
              <HelpIllustration title="Vanliga frågor" />
              <Headings.H1 marginTop={16}>Vanliga frågor</Headings.H1>
            </ModalHeader>
            <ModalBody>{children}</ModalBody>
          </>
        )}
      </ModalContainer>
      <ToggleButton onClick={onToggle} style={{ zIndex: isOpen ? theme.zIndex.modal : theme.zIndex.overlay }}>
        <ToggleIcon isOpen={isOpen} />
      </ToggleButton>
    </>
  );
};
